import React, { useContext } from 'react';
import { Typography, Box, Grid, Container, IconButton, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AuthContext from '../contexts/AuthContext';
import { useQuery, gql } from '@apollo/client';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LabelList } from 'recharts';
import * as XLSX from 'xlsx';
import {
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { AnimatedPaper, StyledTableContainer, StyledButton, tableRowHoverStyles, exportIconButtonStyles } from '../components/StyledComponents';


const ABSENCE_DASHBOARD_STATS = gql`
  query AbsenceDashboardStats {
    absenceDashboardStats {
      absencesByDepartment {
        departmentId
        departmentName
        total
        approved
        pending
        rejected
      }
      absencesByEmployee {
        employeeId
        employeeName
        total
        approved
        pending
        rejected
      }
      absencesByFunction {
        functionId
        functionName
        departmentId
        departmentName
        total
        approved
        pending
        rejected
      }
    }
  }
`;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

function AbsenceDashboard() {
  const { user } = useContext(AuthContext);
  const { data, loading, error } = useQuery(ABSENCE_DASHBOARD_STATS);

  if (loading) return <CircularProgress sx={{ m: 4 }} />;
  if (error) return <Alert severity="error">Erreur: {error.message}</Alert>;

  const { absencesByDepartment, absencesByEmployee, absencesByFunction } = data.absenceDashboardStats;

  // Fonction pour exporter un tableau en Excel
  const exportToExcel = (data, filename, headers) => {
    // Préparer les données avec les en-têtes
    const worksheetData = [
      headers,
      ...data.map(row => {
        const rowData = [];
        headers.forEach(header => {
          // Mapper les clés selon les headers dans l'ordre
          switch(header) {
            case 'Département':
              rowData.push(row.departmentName || '');
              break;
            case 'Employé':
              rowData.push(row.employeeName || '');
              break;
            case 'Fonction':
              rowData.push(row.functionName || '');
              break;
            case 'Total':
              rowData.push(row.total || 0);
              break;
            case 'Approuvée':
              rowData.push(row.approved || 0);
              break;
            case 'En attente':
              rowData.push(row.pending || 0);
              break;
            case 'Rejetée':
              rowData.push(row.rejected || 0);
              break;
            default:
              rowData.push('');
          }
        });
        return rowData;
      })
    ];

    // Créer un workbook et une feuille
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Données');

    // Télécharger le fichier
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Fonctions d'export pour chaque tableau
  const exportDepartmentTable = () => {
    exportToExcel(
      absencesByDepartment,
      'Statistiques_absences_par_departement',
      ['Département', 'Total', 'Approuvée', 'En attente', 'Rejetée']
    );
  };

  const exportEmployeeTable = () => {
    exportToExcel(
      absencesByEmployee,
      'Statistiques_absences_par_employe',
      ['Employé', 'Total', 'Approuvée', 'En attente', 'Rejetée']
    );
  };

  const exportFunctionTable = () => {
    exportToExcel(
      absencesByFunction,
      'Statistiques_absences_par_fonction',
      ['Fonction', 'Total', 'Approuvée', 'En attente', 'Rejetée']
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>Tableau de bord des absences</Typography>
      <Grid container spacing={4}>
        {/* 1. Nombre total d'absences par département */}
        <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Nombre total d'absences du département</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={absencesByDepartment} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="departmentName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#1976d2" name="Total">
                  <LabelList dataKey="total" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid>
        {/* 2. Absences par statut et département */}
        <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Absences par statut et département</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={absencesByDepartment} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="departmentName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" fill="#4caf50" name="Approuvée">
                  <LabelList dataKey="approved" position="top" />
                </Bar>
                <Bar dataKey="pending" fill="#ff9800" name="En attente">
                  <LabelList dataKey="pending" position="top" />
                </Bar>
                <Bar dataKey="rejected" fill="#f44336" name="Rejetée">
                  <LabelList dataKey="rejected" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid>
        {/* 3. Nombre total d'absences par employé */}
        {/* <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6">Nombre total d'absences par employé</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={absencesByEmployee} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="employeeName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#1976d2" name="Total">
                  <LabelList dataKey="total" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid> */}
        {/* 4. Absences par statut et employé */}
        {/* <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6">Absences par statut et employé</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={absencesByEmployee} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="employeeName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" stackId="a" fill="#4caf50" name="Approuvée" />
                <Bar dataKey="pending" stackId="a" fill="#ff9800" name="En attente" />
                <Bar dataKey="rejected" stackId="a" fill="#f44336" name="Rejetée" />
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid> */}
        {/* 5. Nombre total d'absences par fonction */}
        {/* <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6">Nombre total d'absences par fonction</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={absencesByFunction} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="functionName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" fill="#1976d2" name="Total" />
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid> */}
        {/* 6. Absences par statut et fonction */}
        {/* <Grid item xs={12} md={6}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Typography variant="h6">Absences par statut et fonction</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={absencesByFunction} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="functionName" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="approved" stackId="a" fill="#4caf50" name="Approuvée" />
                <Bar dataKey="pending" stackId="a" fill="#ff9800" name="En attente" />
                <Bar dataKey="rejected" stackId="a" fill="#f44336" name="Rejetée" />
              </BarChart>
            </ResponsiveContainer>
          </AnimatedPaper>
        </Grid> */}
      </Grid>
      {/* Statistiques récapitulatifs */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography variant="h4" gutterBottom>Statistiques récapitulatifs</Typography>
        </Grid>
        <Grid item xs={12}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Statistique du département</Typography>
              <IconButton 
                color="primary" 
                onClick={exportDepartmentTable}
                size="small"
                title="Exporter en Excel"
                sx={exportIconButtonStyles}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Département</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Approuvée</TableCell>
                    <TableCell>En attente</TableCell>
                    <TableCell>Rejetée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {absencesByDepartment.map((row, index) => (
                    <TableRow 
                      key={row.departmentId}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        ...tableRowHoverStyles
                      }}
                    >
                      <TableCell>{row.departmentName}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.approved}</TableCell>
                      <TableCell>{row.pending}</TableCell>
                      <TableCell>{row.rejected}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </AnimatedPaper>
        </Grid>
        <Grid item xs={12}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Statistique par employé</Typography>
              <IconButton 
                color="primary" 
                onClick={exportEmployeeTable}
                size="small"
                title="Exporter en Excel"
                sx={exportIconButtonStyles}
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employé</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Approuvée</TableCell>
                    <TableCell>En attente</TableCell>
                    <TableCell>Rejetée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {absencesByEmployee.map((row, index) => (
                    <TableRow 
                      key={row.employeeId}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        ...tableRowHoverStyles
                      }}
                    >
                      <TableCell>{row.employeeName}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.approved}</TableCell>
                      <TableCell>{row.pending}</TableCell>
                      <TableCell>{row.rejected}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </AnimatedPaper>
        </Grid>
        <Grid item xs={12}>
          <AnimatedPaper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Statistique par fonction</Typography>
              <IconButton 
                color="primary" 
                onClick={exportFunctionTable}
                size="small"
                title="Exporter en Excel"
              >
                <FileDownloadIcon />
              </IconButton>
            </Box>
            <StyledTableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fonction</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Approuvée</TableCell>
                    <TableCell>En attente</TableCell>
                    <TableCell>Rejetée</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {absencesByFunction.map((row, index) => (
                    <TableRow 
                      key={row.functionId}
                      sx={{ 
                        backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                        ...tableRowHoverStyles
                      }}
                    >
                      <TableCell>{row.functionName}</TableCell>
                      <TableCell>{row.total}</TableCell>
                      <TableCell>{row.approved}</TableCell>
                      <TableCell>{row.pending}</TableCell>
                      <TableCell>{row.rejected}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </AnimatedPaper>
        </Grid>
      </Grid>
      <Box marginTop={4}>
        <Typography variant="h5" gutterBottom>
          Tableau de bord des absences
        </Typography>
        <Typography paragraph>
          Ce tableau de bord vous permet de visualiser les statistiques des demandes d'absences
          du département, employé et fonction. Utilisez les graphiques et tableaux pour analyser
          les tendances et prendre des décisions éclairées.
        </Typography>
        
        {['ADMIN', 'RH', 'HOST'].includes(user?.role) && (
          <Button
            variant="contained"
            color="primary"
            component={Link}
            to="/absences"
            startIcon={<AdminPanelSettingsIcon />}
            sx={{ mt: 2, mr: 2 }}
          >
            Gestion des absences
          </Button>
        )}
        
        <Button
          variant="outlined"
          color="primary"
          component={Link}
          to="/absence-stats"
          sx={{ mt: 2 }}
        >
          Statistiques détaillées
        </Button>
      </Box>
    </Container>
  );
}

export default AbsenceDashboard; 